import { defineStore } from 'pinia'
import * as Sentry from '@sentry/browser'
import { Call } from '@twilio/voice-sdk'
import { Service } from '@/services/helpers/ServiceHelper'
import useSnackbar from '@/composables/useSnackbar'
import { useCallDevice } from '@/composables/useCallDevice'
import { useCallTimers } from '@/composables/useCallTimers'
import { useCallDisplay } from '@/composables/useCallDisplay'
import { useCallContacts } from '@/composables/useCallContacts'
import type { Contact, CallContact } from '@/services/types/Contact'

export enum CallState {
  RINGING = 'RINGING',
  ACTIVE = 'ACTIVE',
  ON_HOLD = 'ON_HOLD',
  ENDED = 'ENDED',
}

export enum CallType {
  INCOMING = 'INCOMING',
  OUTGOING = 'OUTGOING',
}

export interface ConferenceParticipant {
  callSid: string
  contact: Contact | CallContact | null,
  isOnHold?: boolean
  accepted?: boolean
  declined?: boolean
}

export interface ConferenceEntry {
  // Primary identifier: conference SID
  id: string
  // The client's own call-leg SID (needed for Twilio SDK operations)
  clientCallSid: string | null
  // The Twilio Call object for the client's own leg of the call
  // (only available after accepting an incoming call or initiating an outgoing call)
  call: Call | null
  conferenceParticipants: ConferenceParticipant[]
  state: CallState
  type: CallType
  muted: boolean
  duration: number
  isAddingParticipant: boolean
  isRemovingParticipant: boolean
}

export interface CallUpdatePayload {
  call_sid: string
  call_status: 'ringing' | 'in-progress' | 'completed' | string
  call_direction: 'inbound' | 'outbound-dial' | string
  call_waiting_state?: 'waiting' | string
  from_phone_number?: string
  action?: string
  conference_sid?: string
  ended_conference_sid?: string
  participants?: Array<any>
  participant_number?: string
  target_login?: string
}

export const useCallStore = defineStore('call', () => {
  const snackbar = useSnackbar()

  const {
    callDevice,
    requestMicPermission,
    setCallDevice,
    inputSource,
    availableInputSources,
    changeInputSource,
    outputSource,
    availableOutputSources,
    changeOutputSource,
    stopRingtone
  } = useCallDevice()

  // ── Core multi-call state ──────────────────────────────────────────────
  // Map keyed by conferenceSid (or temporary callSid until conferenceSid is known)
  // shallowRef: updates require reference replacement (`conferences.value = new Map(...)`).
  // Mutations via .set/.delete/.clear will NOT trigger reactivity.
  const conferences = shallowRef<Map<string, ConferenceEntry>>(new Map())
  const activeConferenceId = ref<string | null>(null)
  const heldConferenceId = ref<string | null>(null)
  const incomingRingingCallId = ref<string | null>(null)
  const isEventStreamConnected = ref(false)
  const isConnecting = ref(false)
  const isMergingConferences = ref(false)

  // ── Getters ────────────────────────────────────────────────────────────
  const activeCall = computed(() => (activeConferenceId.value ? conferences.value.get(activeConferenceId.value) ?? null : null))
  const heldCall = computed(() => (heldConferenceId.value ? conferences.value.get(heldConferenceId.value) ?? null : null))
  const ringingCall = computed(() => (incomingRingingCallId.value ? conferences.value.get(incomingRingingCallId.value) ?? null : null))

  // ── Display derivations ────────────────────────────────────────────────
  const {
    isIncomingRinging,
    isNewIncomingCall,
    isOnHold,
    ringingParticipants,
    activeParticipants,
    heldParticipants,
    extraActiveParticipantsText,
    extraHeldParticipantsText,
    callTitle,
    callInProgress,
    dialerOpen,
    callSidebarOpen,
    lastCompletedOutgoingContact
  } = useCallDisplay({ activeCall, heldCall, ringingCall, incomingRingingCallId })

  // ── Per-call timers ────────────────────────────────────────────────────
  const { startCallTimer, stopCallTimer, rekeyTimer } = useCallTimers(conferences)
  const callDuration = computed(() => activeCall.value?.duration ?? 0)

  // ── Call entry helpers ─────────────────────────────────────────────────

  // Find an entry by its Twilio call-leg SID (linear scan).
  const findEntryByCallSid = (callSid: string): ConferenceEntry | undefined => {
    return Array.from(conferences.value.values()).find(e => e.clientCallSid === callSid)
  }

  const createConferenceEntry = (overrides: Partial<ConferenceEntry> & Pick<ConferenceEntry, 'id' | 'type'>) => {
    const entry = {
      clientCallSid: null,
      call: null,
      conferenceParticipants: [],
      state: CallState.RINGING,
      muted: false,
      duration: 0,
      isAddingParticipant: false,
      isRemovingParticipant: false,
      ...overrides
    }
    const updated = new Map(conferences.value)
    updated.set(overrides.id, entry)
    conferences.value = updated
  }

  const patchConferenceEntry = (
    conferenceId: string,
    patch: Partial<ConferenceEntry>,
    map: Map<string, ConferenceEntry> = new Map(conferences.value)
  ) => {
    const entry = map.get(conferenceId)
    if (!entry) { return }
    map.set(conferenceId, { ...entry, ...patch })
    if (map) { conferences.value = map }
  }

  /**
   * Re-key an entry from a temporary key (usually callSid) to the real conferenceSid.
   * Updates all pointer refs (activeConferenceId, heldConferenceId, incomingRingingCallId) accordingly.
   */
  const rekeyConferenceEntry = (oldKey: string, newKey: string): void => {
    if (oldKey === newKey) { return }
    const entry = conferences.value.get(oldKey)
    if (!entry) { return }

    const updated = new Map(conferences.value)
    updated.delete(oldKey)
    updated.set(newKey, { ...entry, id: newKey })
    conferences.value = updated

    rekeyTimer(oldKey, newKey)

    // Update pointer refs
    if (activeConferenceId.value === oldKey) { activeConferenceId.value = newKey }
    if (heldConferenceId.value === oldKey) { heldConferenceId.value = newKey }
    if (incomingRingingCallId.value === oldKey) { incomingRingingCallId.value = newKey }
  }

  const removeConferenceEntry = (conferenceId: string, callSid?: string) => {
    const wasActive = activeConferenceId.value === conferenceId
    const entry = conferences.value.get(conferenceId)
    const updated = new Map(conferences.value)
    if (entry) {
      entry.call?.removeAllListeners()
      stopCallTimer(conferenceId)
      updated.delete(conferenceId)
    } else if (callSid) {
      const entryByCallSid = findEntryByCallSid(callSid)
      if (entryByCallSid) {
        entryByCallSid.call?.removeAllListeners()
        stopCallTimer(entryByCallSid.id)
        updated.delete(entryByCallSid.id)
      }
    }

    conferences.value = updated

    if (activeConferenceId.value === conferenceId) { activeConferenceId.value = null }
    if (heldConferenceId.value === conferenceId) { heldConferenceId.value = null }
    if (incomingRingingCallId.value === conferenceId) { incomingRingingCallId.value = null }

    // Auto-resume held call when the active call drops
    if (wasActive && heldConferenceId.value) {
      resumeCall(heldConferenceId.value)
    }
  }

  // ── Contact lookup ─────────────────────────────────────────────────────
  const { getCallContact, parseConferenceParticipants } = useCallContacts()

  // ── Conference helpers ─────────────────────────────────────────────────

  const updateConferenceParticipants = async (conferenceId: string, participants: Array<any>): Promise<void> => {
    const realParticipants = await parseConferenceParticipants(participants)
    const entry = conferences.value.get(conferenceId)
    if (!entry) { return }
    patchConferenceEntry(conferenceId, { conferenceParticipants: realParticipants })
  }

  const addParticipant = async (conferenceId: string, phoneNumber: string): Promise<void> => {
    const entry = conferences.value.get(conferenceId)
    if (!entry) { return }
    patchConferenceEntry(conferenceId, { isAddingParticipant: true })
    try {
      await Service?.gql?.conferencesGql?.addParticipant(conferenceId, phoneNumber)
    } catch (error) {
      Sentry.captureException(error)
      snackbar.error('Failed to add participant')
    } finally {
      patchConferenceEntry(conferenceId, { isAddingParticipant: false })
    }
  }

  const addParticipantToActiveConference = async (): Promise<void> => {
    const callSid = ringingCall.value?.id
    const entry = conferences.value.get(callSid!)
    if (!entry) { return }
    patchConferenceEntry(callSid!, { isAddingParticipant: true })
    try {
      await Service?.gql?.conferencesGql?.acceptIntoActiveConference(ringingCall.value!.clientCallSid!)
    } catch (error) {
      Sentry.captureException(error)
      snackbar.error('Failed to add participant to active conference')
    } finally {
      patchConferenceEntry(callSid!, { isAddingParticipant: false })
      stopRingtone()
    }
  }

  const removeParticipant = async (conferenceId: string, participantSid: string): Promise<void> => {
    const entry = conferences.value.get(conferenceId)
    if (!entry) { return }
    patchConferenceEntry(conferenceId, { isRemovingParticipant: true })
    try {
      await Service?.gql?.conferencesGql?.removeParticipant(participantSid)
      const patch: Partial<ConferenceEntry> = {
        isRemovingParticipant: false
      }
      patchConferenceEntry(conferenceId, patch)
    } catch (error) {
      Sentry.captureException(error)
      snackbar.error('Failed to remove participant')
      patchConferenceEntry(conferenceId, { isRemovingParticipant: false })
    }
  }

  // ── Incoming call handling ─────────────────────────────────────────────

  const addRingingCallEntry = async (call: Call) => {
    const callerPhoneNumber = call.customParameters.get('caller_number')
    const callSid = call.customParameters.get('caller_call_sid')
    const contact = await getCallContact(callerPhoneNumber!)
    if (!contact) { return }
    if (callSid) {
      createConferenceEntry({
        id: callSid,
        clientCallSid: callSid,
        call,
        conferenceParticipants: [{ callSid, contact }],
        type: CallType.INCOMING,
        state: CallState.RINGING
      })
      incomingRingingCallId.value = callSid
    }
  }

  const acceptRingingCall = () => {
    const entry = conferences.value.get(incomingRingingCallId.value!)
    const key = entry?.id
    if (!key) { return }
    patchConferenceEntry(key, { state: CallState.ACTIVE })
    activeConferenceId.value = key
    incomingRingingCallId.value = null
    startCallTimer(key)
  }

  const processEventStream = async (payload: CallUpdatePayload) => {
    const callSid = payload.call_sid
    const conferenceSid = payload.conference_sid
    if (payload.action === 'incoming_call') {
      const needsRekey = incomingRingingCallId.value === callSid && incomingRingingCallId.value.startsWith('CA')
      if (needsRekey) {
        // The incoming call was first catched by Twilio SDK and
        // now we need to re-key it with the new conferenceSid
        rekeyConferenceEntry(incomingRingingCallId.value!, conferenceSid!)
      } else {
        const isDuplicatedCall = payload.participants?.some((p: any) => p.call_sid === activeConferenceId.value)
        if (activeConferenceId.value && !isDuplicatedCall) {
          const contact = await getCallContact(payload.participant_number ?? '')
          if (!contact) { return }
          createConferenceEntry({
            id: conferenceSid!,
            clientCallSid: callSid,
            conferenceParticipants: [{ callSid, contact }],
            type: CallType.INCOMING,
            state: CallState.RINGING
          })
          incomingRingingCallId.value = conferenceSid!
        }
      }
      return
    }

    if (payload.action === 'incoming_call_ended') {
      const entry = findEntryByCallSid(callSid)
      if (entry) {
        removeConferenceEntry(entry.id)
        incomingRingingCallId.value = null
        stopRingtone()
      }
      return
    }

    if (payload.action === 'conference_merged') {
      const conferenceToRemove = payload.ended_conference_sid
      if (!conferences.value.has(conferenceSid!)) {
        createConferenceEntry({
          id: conferenceSid!,
          clientCallSid: callSid,
          type: CallType.INCOMING,
          state: CallState.ACTIVE
        })
        activeConferenceId.value = conferenceSid!
        startCallTimer(conferenceSid!)
      }
      if (conferences.value.has(conferenceToRemove!)) {
        removeConferenceEntry(conferenceToRemove!)
      }
      updateConferenceParticipants(conferenceSid!, payload.participants!)
    }

    if (payload.action === 'conference_started' || payload.action === 'call_accepted' || payload.action === 'resume') {
      if (!conferences.value.has(conferenceSid!)) {
        rekeyConferenceEntry(activeConferenceId.value!, conferenceSid!)
      }
      patchConferenceEntry(conferenceSid!, { state: CallState.ACTIVE })
      updateConferenceParticipants(conferenceSid!, payload.participants!)
      return
    }

    if (payload.action === 'participant_left' || payload.action === 'participant_joined' || payload.action === 'participant_swapped' || payload.action === 'participant_declined') {
      if (conferences.value.has(conferenceSid!)) {
        updateConferenceParticipants(conferenceSid!, payload.participants!)
      }
      return
    }

    if (payload.action === 'conference_ended') {
      const currentHeldId = heldConferenceId.value
      if (activeConferenceId.value === conferenceSid) {
        await resumeCall(currentHeldId!)
      } else if (currentHeldId === conferenceSid) {
        heldConferenceId.value = null
      }
      removeConferenceEntry(conferenceSid!)
    }
  }

  // ── Accept / Reject Incoming SDK Call ───────────────────────────────────────────
  const acceptIncomingCall = async () => {
    const hasPermission = await requestMicPermission()
    if (!hasPermission) {
      snackbar.error('Microphone permission is required to accept calls')
      await endCall(incomingRingingCallId.value!)
      return
    }
    try {
      ringingCall.value?.call?.accept()
    } catch (error) {
      Sentry.captureException(error)
      snackbar.error('Failed to accept incoming call')
    }
  }

  const clearPastConferenceEntries = async () => {
    try {
      const result = await Service?.gql?.conferencesGql?.getInProgressConferences()
      if (Array.isArray(result) && result.length === 0 && conferences.value.size > 0) {
        conferences.value = new Map()
      }
    } catch (error) {
      Sentry.captureException(error)
      snackbar.error('Failed to fetch in-progress conferences')
    }
  }

  // ── Outgoing call ──────────────────────────────────────────────────────
  const makeOutgoingCall = async (contact: Contact, from: string) => {
    try {
      await clearPastConferenceEntries()
      if (conferences.value.size > 0 || isConnecting.value) {
        snackbar.error('Cannot make a new call while another call is in progress')
        return
      }
      dialerOpen.value = false
      isConnecting.value = true
      await clearAll()

      const hasPermission = await requestMicPermission()
      if (!hasPermission) {
        snackbar.error('Microphone permission is required to make calls')
        return
      }

      const rawCall = await callDevice.value?.connect({ params: { To: contact.phone, From: from } })
      if (!rawCall) {
        snackbar.error('Failed to connect call device, please refresh and try again')
        return
      }

      const callSid = await new Promise<string>((resolve) => {
        rawCall.on('ringing', () => resolve(rawCall.parameters.CallSid))
      })
      if (!callSid) {
        snackbar.error('Failed to initiate outgoing call')
        return
      }
      // Temporarily key by callSid until we resolve the conferenceSid
      createConferenceEntry({
        id: callSid,
        clientCallSid: callSid,
        call: rawCall,
        conferenceParticipants: [{ callSid, contact }],
        type: CallType.OUTGOING,
        state: CallState.RINGING
      })
      activeConferenceId.value = callSid

      rawCall.on('error', (error) => {
        Sentry.captureException(error)
        snackbar.error('There was an error with the outgoing call')
        const current = findEntryByCallSid(callSid)
        removeConferenceEntry(current?.id ?? callSid)
      })

      startCallTimer(callSid)
    } catch (error) {
      Sentry.captureException(error)
      snackbar.error('Failed to make outgoing call')
      const outId = activeCall.value?.type === CallType.OUTGOING ? activeConferenceId.value : null
      if (outId) { removeConferenceEntry(outId) }
    } finally {
      isConnecting.value = false
    }
  }

  // ── Mute / Hold / Resume ───────────────────────────────────────────────
  const toggleMuteActiveCall = () => {
    if (!activeConferenceId.value) { return }
    const entry = conferences.value.get(activeConferenceId.value)
    if (!entry) { return }
    const muted = !entry.muted
    entry.call?.mute(muted)
    const updated = new Map(conferences.value)
    updated.set(activeConferenceId.value, { ...entry, muted })
    conferences.value = updated
  }

  const holdCall = (conferenceId: string): void => {
    const entry = conferences.value.get(conferenceId)
    if (!entry) { return }
    try {
      stopCallTimer(conferenceId)
      patchConferenceEntry(conferenceId, { state: CallState.ON_HOLD })
      if (activeConferenceId.value === conferenceId) { activeConferenceId.value = null }
      heldConferenceId.value = conferenceId
    } catch (error) {
      Sentry.captureException(error)
      snackbar.error('Failed to hold call')
    }
  }

  const resumeCall = (conferenceId: string) => {
    const entry = conferences.value.get(conferenceId)
    if (!entry) { return }
    try {
      patchConferenceEntry(conferenceId, { state: CallState.ACTIVE })
      activeConferenceId.value = conferenceId
      if (heldConferenceId.value === conferenceId) { heldConferenceId.value = null }
      startCallTimer(conferenceId)
    } catch (error) {
      Sentry.captureException(error)
      snackbar.error('Failed to resume call')
    }
  }

  // ── Merge / Swap ───────────────────────────────────────────────────────
  const mergeCalls = async (): Promise<void> => {
    const currentActiveId = activeConferenceId.value
    const currentHeldId = heldConferenceId.value
    if (!currentActiveId || !currentHeldId) { return }

    const activeEntry = conferences.value.get(currentActiveId)
    const heldEntry = conferences.value.get(currentHeldId)
    if (!activeEntry || !heldEntry) { return }

    isMergingConferences.value = true
    try {
      // Send conferenceSids to the backend to merge
      await Service?.gql?.conferencesGql?.mergeCalls(
        currentActiveId,
        currentHeldId
      )
      await resumeCall(currentHeldId)
    } catch (error) {
      Sentry.captureException(error)
      snackbar.error('Failed to merge calls')
    } finally {
      isMergingConferences.value = false
    }
  }

  const swapCalls = async ({ endActive = false } = {}): Promise<void> => {
    const currentActiveId = activeConferenceId.value
    const currentHeldId = heldConferenceId.value
    if (!currentActiveId || !currentHeldId) { return }

    await Service?.gql?.conferencesGql?.swapCalls(currentHeldId!)

    if (endActive) {
      removeConferenceEntry(currentActiveId)
      incomingRingingCallId.value = null
    } else {
      holdCall(currentActiveId)
    }

    await resumeCall(currentHeldId)
  }

  // ── Voicemail / Hold-and-Accept / End-and-Accept ───────────────────────
  const sendToVoicemail = async (conferenceSid: string): Promise<void> => {
    const entry = conferences.value.get(conferenceSid)
    if (!entry) { return }
    const callSid = entry.clientCallSid
    if (callSid) {
      try {
        await Service?.gql?.conferencesGql?.sendToVoicemail(callSid)
        incomingRingingCallId.value = null
        removeConferenceEntry(conferenceSid)
      } catch (error) {
        Sentry.captureException(error)
        snackbar.error('Failed to send call to voicemail')
      } finally {
        stopRingtone()
      }
    }
  }

  const activateIncomingCall = () => {
    const key = activeConferenceId.value!
    patchConferenceEntry(key, { state: CallState.ACTIVE })
    startCallTimer(key)
    incomingRingingCallId.value = null
  }

  const holdAndAccept = async (): Promise<void> => {
    if (!activeConferenceId.value || !incomingRingingCallId.value) { return }
    const ringingId = incomingRingingCallId.value
    const ringingEntry = conferences.value.get(ringingId)
    // sendToVoicemail and acceptAndHold use the callSid for the ringing call
    const ringingCallSid = ringingEntry?.clientCallSid ?? ringingId
    try {
      await Service?.gql?.conferencesGql?.acceptAndHold(ringingCallSid)
      heldConferenceId.value = activeConferenceId.value
      activeConferenceId.value = ringingId
      patchConferenceEntry(heldConferenceId.value, { state: CallState.ON_HOLD })
      stopCallTimer(heldConferenceId.value)
      await activateIncomingCall()
    } catch (error) {
      Sentry.captureException(error)
      snackbar.error('Failed to hold waiting call')
    } finally {
      stopRingtone()
    }
  }

  const endAndAccept = async (): Promise<void> => {
    if (!incomingRingingCallId.value) { return }
    const ringingId = incomingRingingCallId.value
    const ringingEntry = conferences.value.get(ringingId)
    const ringingCallSid = ringingEntry?.clientCallSid ?? ringingId
    try {
      await Service?.gql?.conferencesGql?.acceptAndDrop(ringingCallSid)
      activeConferenceId.value = ringingId
      await activateIncomingCall()
    } catch (error) {
      Sentry.captureException(error)
      snackbar.error('Failed to accept waiting call')
    } finally {
      stopRingtone()
    }
  }

  // ── End or Leave Call / Conference ──────────────────────────────────────────────
  const leaveCall = async () => {
    const conferenceToDropId = activeConferenceId.value
    if (conferenceToDropId) {
      try {
        await Service?.gql?.conferencesGql?.leaveConference(conferenceToDropId)
        removeConferenceEntry(conferenceToDropId)
      } catch (error) {
        Sentry.captureException(error)
        snackbar.error('Failed to leave conference')
      }
    }
  }

  const endCall = async (conferenceId: string) => {
    const entry = conferences.value.get(conferenceId)
    if (!entry) { return }
    if (entry.type === CallType.INCOMING && entry.state === CallState.RINGING) {
      // The user is rejecting an incoming call
      entry.call?.reject()
      await sendToVoicemail(conferenceId)
      return
    }
    if (entry.type === CallType.OUTGOING && entry.state === CallState.RINGING) {
      // The caller is ending the conference before the callee answers
      // or the callee is sending the caller to the voicemail
      entry.call?.disconnect()
      await Service?.gql?.conferencesGql?.endConference({ callSid: conferenceId })
      removeConferenceEntry(conferenceId)
      return
    }
    await endConference(conferenceId)
  }

  const endConference = async (conferenceId: string): Promise<void> => {
    try {
      await Service?.gql?.conferencesGql?.endConference({ conferenceSid: conferenceId })
      if (heldConferenceId.value) {
        await resumeCall(heldConferenceId.value)
      }
      removeConferenceEntry(conferenceId)
    } catch (error) {
      Sentry.captureException(error)
      snackbar.error('Failed to end conference')
    }
  }

  // ── Full reset ─────────────────────────────────────────────────────────
  const clearAll = async (): Promise<void> => {
    callDevice.value?.disconnectAll()
    conferences.value.forEach(entry => entry.call?.removeAllListeners())
    conferences.value = new Map()
    activeConferenceId.value = null
    heldConferenceId.value = null
    incomingRingingCallId.value = null
    stopCallTimer()

    if (callDevice.value) {
      callDevice.value.removeAllListeners()
      callDevice.value.destroy()
      callDevice.value = null
      await setCallDevice()
    }
  }

  // ── Public API ─────────────────────────────────────────────────────────
  return {
    // State
    callDevice,
    callDuration,
    activeConferenceId,
    heldConferenceId,
    incomingRingingCallId,
    isEventStreamConnected,
    isConnecting,
    isMergingConferences,
    // Getters
    activeCall,
    heldCall,
    ringingCall,
    isIncomingRinging,
    isNewIncomingCall,
    isOnHold,
    ringingParticipants,
    activeParticipants,
    extraActiveParticipantsText,
    heldParticipants,
    extraHeldParticipantsText,
    callTitle,
    callInProgress,
    callSidebarOpen,
    dialerOpen,
    lastCompletedOutgoingContact,
    // Actions
    setCallDevice,
    processEventStream,
    acceptIncomingCall,
    toggleMuteActiveCall,
    makeOutgoingCall,
    swapCalls,
    sendToVoicemail,
    holdAndAccept,
    endAndAccept,
    mergeCalls,
    addParticipant,
    removeParticipant,
    addParticipantToActiveConference,
    endCall,
    leaveCall,
    // Audio
    inputSource,
    availableInputSources,
    changeInputSource,
    outputSource,
    availableOutputSources,
    changeOutputSource,
    addRingingCallEntry,
    acceptRingingCall
  }
})
